
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import Reveal from "@/components/Reveal";
import { useLanguage } from "@/contexts/LanguageContext";
import ServiceCard from "@/components/features/services/ServiceCard";
import { fetchServices } from "@/services/services-api";
import type { ServiceData } from "@/types/services";

const ServicesSection = () => {
  const { t, language } = useLanguage();
  const isRTL = language === "ar";
  const [services, setServices] = useState<ServiceData[]>([]);

  useEffect(() => {
    let active = true;

    fetchServices(language)
      .then((items) => active && setServices(items))
      .catch(() => active && setServices([]));

    return () => {
      active = false;
    };
  }, [language]);

  const displayedServices = services.slice(0, 6);
  const cardDirections: Array<"bottom" | "left" | "right"> = ["bottom", "left", "right", "bottom", "left", "right"];

  return (
    <section className={`py-16 md:py-24 bg-white overflow-hidden ${isRTL ? "rtl" : "ltr"}`} id="services" dir={isRTL ? "rtl" : "ltr"}>
      <div className="container">
        <Reveal className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-nexus-gray mb-4">{t("services_section.title")}</h2>
          <p className="text-gray-600">{t("services_section.description")}</p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayedServices.map((service, index) => (
            <Reveal key={service.id} delay={index * 120} from={cardDirections[index]} className="h-full">
              <ServiceCard service={service} className="h-full card-lift" />
            </Reveal>
          ))}
        </div>

        <Reveal delay={300} className="mt-12 text-center">
          <Button asChild className="bg-nexus-brand hover:bg-nexus-brand/90 dark:text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-nexus-brand/30">
            <Link to="/services">
              {language === "ar" ? (
                <>
                  {t("services_section.viewAll")}
                  <ArrowRight className="mr-2 h-4 w-4 transform rotate-180" />
                </>
              ) : (
                <>
                  {t("services_section.viewAll")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Link>
          </Button>
        </Reveal>
      </div>
    </section>
  );
};

export default ServicesSection;
